import { useCallback, useEffect, useMemo, useState } from 'react';
import { useGameStore } from '../../store/gameSlice';
import { useInventoryStore } from '../../store/inventorySlice';
import { useShopPurchasesStore } from '../../store/shopPurchasesSlice';
import { useShopStockStore } from '../../store/shopStockSlice';
import { useSpellCatalogStore } from '../../store/spellCatalogueSlice';
import { useLoadoutStore } from '../../store/loadoutSlice';
import { closeInterface } from '../../services/InterfaceService';
import * as contentUnlock from '../../services/ContentUnlockService';
import { SHOP_REGISTRY } from '../../data/shops.config';
import { TRINKET_MINOR_REGISTRY } from '../../data/trinkets.config';
import { INVENTORY_COLS, ITEMS } from '../../data/inventory.config';
import { UI_FORGE_GRID_FRAME_REGION } from '../../data/ui.atlas';
import { ensureShopStock, getItemPrice } from '../../services/ShopService';
import { describeItemCard } from '../../services/CardDescriptionService';
import {
  getUnlockedSpellTargets,
  isSinglePurchaseItem,
} from '../../utils/item.utils';
import type { ItemDefinition } from '../../types/inventory.types';
import { KeyDisplay } from './KeyDisplay';
import { NineSliceFrame } from './NineSliceFrame';
import { ConfirmPopup } from './ConfirmPopup';
import { ItemList, ItemListEntry } from './ItemList';
import { audioService } from '../../services/AudioService';
import './ShopScreen.scss';

type Side = 'buy' | 'sell';

// Visible rows per side; the inventory windows past this (offset, no scrollbar)
// like the chest, so a full inventory stays navigable. Cf. ChestScreen.
const VISIBLE_ROWS = 4;

/**
 * Single-purchase items (grimoires): each target spell can be bought only once
 * across the whole save (cf. `useShopPurchasesStore.purchasedSpellUnlocks`).
 * This is independent of the buying shop and of whether the player has used or
 * dropped the grimoire — a grimoire for a spell bought at any shop disappears
 * from ALL shops.
 *
 * Cf. docs/game-design/hub.md and docs/hub.md.
 */
export const ShopScreen = () => {
  const currentShopId = useGameStore((s) => s.currentShopId);
  const activeInterface = useGameStore((s) => s.activeInterface);
  const setCurrentShopId = useGameStore((s) => s.setCurrentShopId);
  const slots = useInventoryStore((s) => s.slots);
  const goldAvailable = useInventoryStore((s) => s.gold);
  const addItem = useInventoryStore((s) => s.addItem);
  const removeItem = useInventoryStore((s) => s.removeItem);
  // B8 fallback: locations like the Wandering Merchant open their interfaceId
  // (= shopId) directly, without going through an NPC that calls
  // setCurrentShopId. Fall back to the current interfaceId if it is registered
  // in SHOP_REGISTRY.
  const shopId =
    currentShopId ??
    (activeInterface && SHOP_REGISTRY[activeInterface]
      ? activeInterface
      : null);

  const purchasedSpellUnlocks = useShopPurchasesStore(
    (s) => s.purchasedSpellUnlocks,
  );
  const recordSpellUnlockPurchase = useShopPurchasesStore(
    (s) => s.recordSpellUnlockPurchase,
  );

  const shop = shopId ? SHOP_REGISTRY[shopId] : null;
  const isRolling = !!shop?.rolling;

  const [side, setSide] = useState<Side>('buy');

  // Rolling shop: the displayed stock comes from `useShopStockStore` (a random
  // per-run-cycle draw), not from the static `shop.offers` pool.
  const shopOffers = useShopStockStore((s) => s.offers);
  const soldItemIds = useShopStockStore((s) => s.soldItemIds);
  const recordSold = useShopStockStore((s) => s.recordSold);

  // Minor-trinket offers (e.g. blank-charm) unlock in the loadout instead of
  // adding an inventory item.
  const minorTrinkets = useLoadoutStore((s) => s.minorTrinkets);

  useEffect(() => {
    if (shop?.rolling) {
      ensureShopStock(shop.id);
    }
  }, [shop]);

  const baseOffers = useMemo(
    () => (isRolling ? shopOffers : (shop?.offers ?? [])),
    [isRolling, shopOffers, shop],
  );

  const close = useCallback(() => {
    setCurrentShopId(null);
    closeInterface();
  }, [setCurrentShopId]);

  // Filters out grimoires whose at least one target spell has already been
  // bought at any shop (global limit, not per shop). Independent of the spell
  // unlock catalog — the player may own the spell via drop without having bought
  // the grimoire.
  const visibleOffers = useMemo(() => {
    if (!shop) {
      return [];
    }
    return baseOffers
      .map((offer, originalIdx) => ({ offer, originalIdx }))
      .filter(({ offer }) => {
        // Rolling shop: hide items already bought this cycle.
        if (isRolling && soldItemIds.includes(offer.itemId)) {
          return false;
        }
        if (offer.kind === 'minor') {
          return minorTrinkets[offer.itemId] === undefined;
        }
        const def = ITEMS[offer.itemId];
        if (!def) {
          return false;
        }
        const targets = getUnlockedSpellTargets(def);
        if (targets.length === 0) {
          return true;
        }
        return !targets.some((spellId) =>
          purchasedSpellUnlocks.includes(spellId),
        );
      });
  }, [
    shop,
    baseOffers,
    isRolling,
    soldItemIds,
    purchasedSpellUnlocks,
    minorTrinkets,
  ]);

  const unlockedSpells = useSpellCatalogStore((s) => s.unlockedSpells);

  // Everything in the inventory is sellable (any shop buys anything), except
  // gold itself. Selling a grimoire of an unknown spell is allowed but goes
  // through a confirmation popup (see `needsSellConfirm` / `sell`).
  const canSell = useCallback(
    (def: ItemDefinition | undefined): boolean =>
      def !== undefined && def.id !== 'gold',
    [],
  );

  // A grimoire whose target spell isn't unlocked yet warrants a confirmation:
  // selling it forfeits the chance to learn the spell.
  const needsSellConfirm = useCallback(
    (def: ItemDefinition): boolean =>
      getUnlockedSpellTargets(def).some(
        (spellId) => !unlockedSpells.includes(spellId),
      ),
    [unlockedSpells],
  );

  const [pendingSale, setPendingSale] = useState<{
    itemId: string;
    amount: number;
  } | null>(null);

  const buy = useCallback(
    (offerIdx: number): boolean => {
      if (!shop) {
        return false;
      }
      const offer = baseOffers[offerIdx];
      if (!offer) {
        return false;
      }
      const price = getItemPrice(offer);
      if (goldAvailable < price) {
        return false;
      }
      // Minor-trinket offer: unlock via ContentUnlockService (unlock +
      // showcase), no inventory item.
      if (offer.kind === 'minor') {
        if (!contentUnlock.unlockMinor(offer.itemId)) {
          return false;
        }
        removeItem('gold', price);
        audioService.playSfx('buy');
        if (isRolling) {
          recordSold(offer.itemId);
        }
        return true;
      }
      const def = ITEMS[offer.itemId];
      if (!def) {
        return false;
      }
      const targets = getUnlockedSpellTargets(def);
      // Defense in depth: block an already-bought spell in case the UI shows a
      // stale offer (desynchronized state).
      if (targets.some((id) => purchasedSpellUnlocks.includes(id))) {
        return false;
      }
      const qty = offer.quantity ?? 1;
      const leftover = addItem(offer.itemId, qty);
      if (leftover === qty) {
        // Inventory is full — abort the purchase.
        return false;
      }
      removeItem('gold', price);
      audioService.playSfx('buy');
      // Mark each target spell as bought (covers the unlikely-but-possible case
      // of a grimoire that unlocks several spells).
      if (isSinglePurchaseItem(def)) {
        for (const spellId of targets) {
          recordSpellUnlockPurchase(spellId);
        }
      }
      // Rolling shop: one purchase per item per cycle — remove it from stock.
      if (isRolling) {
        recordSold(offer.itemId);
      }
      return true;
    },
    [
      shop,
      baseOffers,
      isRolling,
      recordSold,
      goldAvailable,
      addItem,
      removeItem,
      purchasedSpellUnlocks,
      recordSpellUnlockPurchase,
    ],
  );

  const doSell = useCallback(
    (itemId: string, amount: number): boolean => {
      const def = ITEMS[itemId];
      if (!canSell(def) || amount <= 0) {
        return false;
      }
      const removed = removeItem(itemId, amount);
      if (removed > 0) {
        addItem('gold', def.sellValue * removed);
        return true;
      }
      return false;
    },
    [addItem, removeItem, canSell],
  );

  const sell = useCallback(
    (itemId: string, amount: number): boolean => {
      const def = ITEMS[itemId];
      if (!def || !canSell(def) || amount <= 0) {
        return false;
      }
      if (needsSellConfirm(def)) {
        setPendingSale({ itemId, amount });
        return true;
      }
      return doSell(itemId, amount);
    },
    [canSell, needsSellConfirm, doSell],
  );

  const switchSide = useCallback(
    () => setSide((s) => (s === 'buy' ? 'sell' : 'buy')),
    [],
  );

  const shopItems = useMemo<(ItemListEntry | null)[]>(
    () =>
      visibleOffers.map(({ offer, originalIdx }) => {
        if (offer.kind === 'minor') {
          const tdef = TRINKET_MINOR_REGISTRY[offer.itemId];
          if (!tdef) {
            return null;
          }
          const price = getItemPrice(offer);
          return {
            id: offer.itemId,
            navId: `buy:${originalIdx}`,
            icon: tdef.icon ?? '',
            cost: price,
            dimmed: goldAvailable < price,
            card: {
              icon: tdef.icon ?? '',
              name: tdef.name,
              subtitle: `Babiole vierge · ${tdef.socketSlots ?? 0} sockets`,
            },
          };
        }
        const def = ITEMS[offer.itemId];
        if (!def) {
          return null;
        }
        const price = getItemPrice(offer);
        const qty = offer.quantity ?? 1;
        return {
          id: offer.itemId,
          navId: `buy:${originalIdx}`,
          icon: def.sprite,
          overlay: def.iconOverlay,
          cost: price,
          dimmed: goldAvailable < price,
          quantity: qty > 1 ? qty : undefined,
          card: {
            icon: def.sprite,
            overlay: def.iconOverlay,
            name: def.label,
            subtitle: def.description,
            ...(describeItemCard(offer.itemId) ?? {}),
          },
        };
      }),
    [visibleOffers, goldAvailable],
  );

  const invItems = useMemo<(ItemListEntry | null)[]>(
    () =>
      slots.map((slot, idx) => {
        const def = slot ? ITEMS[slot.itemId] : null;
        if (!slot || !def) {
          return null;
        }
        return {
          id: `inv:${idx}`,
          navId: `inv:${idx}`,
          icon: def.sprite,
          overlay: def.iconOverlay,
          quantity: def.maxStack > 1 ? slot.quantity : undefined,
          dimmed: !canSell(def),
          card: {
            icon: def.sprite,
            overlay: def.iconOverlay,
            name: def.label,
            subtitle: def.description,
            ...(describeItemCard(slot.itemId) ?? {}),
          },
        };
      }),
    [slots, canSell],
  );

  const sellSlot = useCallback(
    (navId: string, whole: boolean): boolean => {
      const idx = parseInt(navId.split(':')[1], 10);
      const slot = Number.isNaN(idx) ? null : slots[idx];
      if (!slot) {
        return false;
      }
      return sell(slot.itemId, whole ? slot.quantity : 1);
    },
    [slots, sell],
  );

  if (!shop) {
    return null;
  }

  return (
    <div className="shop-overlay">
      <div className="shop-screen">
        <div className="shop-header">
          <h1>{shop.label}</h1>
          <div className="shop-gold">{goldAvailable} or</div>
        </div>

        <div className="shop-columns">
          <section className={`shop-side${side === 'buy' ? ' is-active' : ''}`}>
            <h3 className="shop-side-title">Acheter</h3>
            <NineSliceFrame className="shop-grid-frame">
              <ItemList
                active={side === 'buy' && !pendingSale}
                items={shopItems}
                cols={INVENTORY_COLS}
                maxRows={VISIBLE_ROWS}
                onConfirm={(item) =>
                  buy(parseInt(item.navId.split(':')[1], 10))
                }
                onCancel={close}
                onPause={close}
                onTabNext={switchSide}
                onTabPrev={switchSide}
              />
            </NineSliceFrame>
          </section>

          <section
            className={`shop-side${side === 'sell' ? ' is-active' : ''}`}
          >
            <h3 className="shop-side-title">Vendre</h3>
            <NineSliceFrame
              region={UI_FORGE_GRID_FRAME_REGION}
              className="shop-grid-frame"
            >
              <ItemList
                active={side === 'sell' && !pendingSale}
                items={invItems}
                cols={INVENTORY_COLS}
                maxRows={VISIBLE_ROWS}
                onConfirm={(item) => sellSlot(item.navId, false)}
                onSecondary={(item) => sellSlot(item.navId, true)}
                onCancel={close}
                onPause={close}
                onTabNext={switchSide}
                onTabPrev={switchSide}
              />
            </NineSliceFrame>
          </section>
        </div>

        <div className="shop-hint">
          <KeyDisplay context="interface" action="CONFIRM" size="sm" /> Acheter
          / vendre une unité ·{' '}
          <KeyDisplay context="interface" action="SECONDARY" size="sm" /> Vendre
          la pile ·{' '}
          <KeyDisplay context="interface" action="DETAILS" size="sm" /> Détails
          ·{' '}
          <span className="shop-hint-tabs">
            <KeyDisplay context="interface" action="TAB_PREV" size="sm" />
            <KeyDisplay context="interface" action="TAB_NEXT" size="sm" />
          </span>{' '}
          Changer de côté ·{' '}
          <KeyDisplay context="interface" action="CANCEL" size="sm" /> Retour
        </div>
      </div>
      {pendingSale && (
        <ConfirmPopup
          message="Vendre ce grimoire ? Tu ne connais pas encore ce sort — tu perds la chance de l'apprendre."
          confirmLabel="Vendre"
          cancelLabel="Annuler"
          onConfirm={() => {
            doSell(pendingSale.itemId, pendingSale.amount);
            setPendingSale(null);
          }}
          onCancel={() => setPendingSale(null)}
        />
      )}
    </div>
  );
};
